import React from 'react'
import { motion } from 'framer-motion'
import { FaCcAmex, FaCcMastercard, FaCcVisa, FaCcApplePay, FaGooglePay, FaShieldAlt, FaLock, FaCreditCard } from 'react-icons/fa'

const paymentIcons = [
    { Icon: FaCcVisa,       label: 'Visa',       color: 'text-[#1A1F71]' },
    { Icon: FaCcMastercard, label: 'Mastercard',  color: 'text-[#EB001B]' },
    { Icon: FaCcAmex,       label: 'Amex',        color: 'text-[#006FCF]' },
    { Icon: FaCcApplePay,   label: 'Apple Pay',   color: 'text-gray-800' },
    { Icon: FaGooglePay,    label: 'Google Pay',  color: 'text-gray-700' },
]

const securityFeatures = [
    {
        icon: <FaShieldAlt className="text-orange-500" />,
        title: 'Fraud & Scam Protection',
        desc: 'Stay protected from frauds and scams with our secure payment infrastructure.',
    },
    {
        icon: <FaLock className="text-orange-500" />,
        title: 'TLS 1.3 Encryption',
        desc: 'StringsSG secures your payment by the trusted Transport Layer Security (TLS 1.3), the highest level of network protection.',
    },
    {
        icon: <FaCreditCard className="text-orange-500" />,
        title: 'Multiple Payment Options',
        desc: 'Pay with PayNow, Mastercard, Visa, American Express, GrabPay and many more.',
    },
]

const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } },
}

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
}

export const SecurePayment = () => {
    return (
        <section className="relative py-14 md:py-20 overflow-hidden">
            {/* Ambient blobs */}
            <div className="absolute -top-20 left-1/4 w-80 h-80 bg-orange-100/35 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 right-1/4 w-72 h-72 bg-amber-50/40 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-md border border-orange-200/60 px-4 py-1.5 rounded-full mb-5 shadow-sm">
                        <span className="text-sm">🔒</span>
                        <span className="text-xs font-semibold text-orange-600 uppercase tracking-wider">Secure Payments</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
                        Secure Payment Options
                    </h2>
                </div>

                {/* Payment icons row */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                    className="flex items-center justify-center gap-3 md:gap-5 mb-10"
                >
                    {paymentIcons.map(({ Icon, label, color }) => (
                        <motion.div
                            key={label}
                            variants={itemVariants}
                            className="group flex items-center justify-center w-16 h-12 md:w-20 md:h-14 rounded-2xl bg-white/55 backdrop-blur-xl border border-white/70 shadow-[0_4px_20px_rgba(251,146,60,0.06)] hover:shadow-[0_6px_28px_rgba(251,146,60,0.12)] hover:-translate-y-0.5 hover:border-orange-200/50 transition-all duration-300"
                        >
                            <Icon className={`text-3xl md:text-4xl ${color} opacity-75 group-hover:opacity-100 transition-opacity duration-300`} />
                        </motion.div>
                    ))}
                </motion.div>

                {/* Security features */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                    className="grid md:grid-cols-3 gap-4"
                >
                    {securityFeatures.map((feature) => (
                        <motion.div
                            key={feature.title}
                            variants={itemVariants}
                            className="group rounded-2xl p-5 md:p-6 bg-white/50 backdrop-blur-xl border border-white/70 shadow-[0_4px_24px_rgba(251,146,60,0.05)] hover:shadow-[0_8px_32px_rgba(251,146,60,0.12)] hover:-translate-y-0.5 hover:border-orange-200/50 transition-all duration-400"
                        >
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-orange-50 border border-orange-100/60 mb-4 group-hover:bg-orange-100 transition-colors duration-300">
                                {feature.icon}
                            </div>
                            <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1.5 group-hover:text-orange-600 transition-colors duration-300">
                                {feature.title}
                            </h3>
                            <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                                {feature.desc}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    )
}
